import BackIcon from '@cp/src/assets/images/icons/BackIcon';
import styles from './index.module.scss';
import { Button, Typography } from '@navi/web-ui/lib/primitives';
import { useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import NaviTable from '@cp/src/components/NaviTable/NaviTable';
import { PaginationState } from '@tanstack/react-table';
import AddConfigurationDrawer from './drawer/AddConfigurationDrawer';
import EditConfigurationDrawer from './drawer/EditConfigurationDrawer';
import { getAllocationColumnDefs } from './allocationColDefs';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllCiuAllocationConfigs,
  getAllocationStats,
  getCiuAllocationConfigById,
  getPreviewQuery
} from './action';
import {
  setPagination,
  setEditConfigurationData,
  setSelectedConfigurationId
} from './ciuAllocationSlice';
import { RootState } from '@cp/src/store';
import { getLatestCiuAllocationConfig } from './action';
import { AllocationData, ConfigurationFormData } from './types';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import SuspenseLoader from '@cp/src/components/SkeletonLoader/SuspenseLoader';
import LineLoader from '@cp/src/components/SkeletonLoader/LineLoader';

const CiuAllocations = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const pageSize = useSelector((state: RootState) => state.ciuAllocations.pagination?.pageSize);
  const pageIndex = useSelector((state: RootState) => state.ciuAllocations.pagination?.pageIndex);
  const totalElements = useSelector(
    (state: RootState) => state.ciuAllocations.pagination?.totalElements
  );

  const businessVertical = useSelector((store: RootState) => store?.common?.agentBusinessVertical);

  const editConfigurationData = useSelector(
    (state: RootState) => state.ciuAllocations?.editConfigurationData
  );

  const allocationData = useSelector(
    (state: RootState) => state.ciuAllocations?.configurationSections
  );

  const isTableLoading = useSelector((state: RootState) => state.ciuAllocations.isTableLoading);
  const allocationStats = useSelector((state: RootState) => state.ciuAllocations.allocationStats);
  const isStatsLoading = useSelector((state: RootState) => state.ciuAllocations.isStatsLoading);

  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);

  const goBack = () => {
    navigate(-1);
  };

  const handleNewConfigurationClick = () => {
    dispatch(getLatestCiuAllocationConfig(businessVertical));
    setIsAddDrawerOpen(true);
  };

  const onEdit = (id: string) => {
    dispatch(getCiuAllocationConfigById(id, businessVertical));
    setIsEditDrawerOpen(true);
  };

  const onDuplicate = (id: string) => {
    dispatch(getCiuAllocationConfigById(id, businessVertical, true));
    setIsAddDrawerOpen(true);
  };

  const columns = getAllocationColumnDefs(onEdit, onDuplicate);

  useEffect(() => {
    dispatch(getAllCiuAllocationConfigs(pageIndex, pageSize, businessVertical));
    dispatch(getAllocationStats(businessVertical));
  }, []);

  const handlePaginationChange = (newPagination: PaginationState) => {
    dispatch(
      setPagination({
        pageIndex: newPagination.pageIndex,
        pageSize: newPagination.pageSize
      })
    );
    dispatch(
      getAllCiuAllocationConfigs(newPagination.pageIndex, newPagination.pageSize, businessVertical)
    );
  };

  const handleSuccessClick = () => {
    dispatch(getAllCiuAllocationConfigs(pageIndex, pageSize, businessVertical));
  };

  const handleQueryCopy = (formData?: ConfigurationFormData) => {
    if (formData) {
      dispatch(getPreviewQuery(formData, businessVertical));
    } else {
      toast.error('Please fill the form first');
    }
  };

  const handleCloseEditDrawer = () => {
    setIsEditDrawerOpen(false);
    dispatch(setEditConfigurationData(null));
    dispatch(setSelectedConfigurationId(null));
  };

  const handleEditDrawerSuccess = () => {
    handleSuccessClick();
    dispatch(setEditConfigurationData(null));
    dispatch(setSelectedConfigurationId(null));
  };

  return (
    <div className={styles.container}>
      <div className={styles.topNavContainer}>
        <div className="flex items-center gap-2">
          <BackIcon className="cursor-pointer" size={'16'} onClick={goBack} />
          <Typography variant="h2" color="var(--light-grayscale-content-2)">
            CIU allocation
          </Typography>
        </div>
      </div>

      <div className={styles.statsContainer}>
        <SuspenseLoader
          loading={!!isStatsLoading}
          fallBack={
            <div className={styles.statsSkeletonContainer}>
              <div className={styles.card}>
                <LineLoader containerStyle="w-[180px]" numberOfLines={2} />
              </div>
              <div className={styles.card}>
                <LineLoader containerStyle="w-[180px] " numberOfLines={2} />
              </div>
            </div>
          }
        >
          <div className={styles.card}>
            <Typography variant="h2" color="var(--light-grayscale-content-1)">
              {allocationStats?.totalActiveAllocations || 0}
            </Typography>
            <Typography variant="p5" color="var(--green-dark-bg)">
              Current allocated cases
            </Typography>
          </div>
          <div className={styles.card}>
            <Typography variant="h2" color="var(--navi-color-orange-base)">
              {allocationStats?.allocationsExpiringToday || 0}
            </Typography>
            <Typography variant="p5" color="var(--green-dark-bg)">
              Cases expiring today
            </Typography>
          </div>
        </SuspenseLoader>
        <Button
          variant="primary"
          className={styles.newConfigButton}
          onClick={handleNewConfigurationClick}
        >
          New configuration
        </Button>
      </div>

      <div className={styles.tableContainer}>
        <NaviTable
          columns={columns}
          data={allocationData || []}
          totalElements={totalElements}
          defaultPaginationState={{
            pageIndex: 0,
            pageSize: 10
          }}
          onPaginationChange={handlePaginationChange}
          tableClasses={styles.table}
          showPaginationComponent={true}
          loading={isTableLoading}
        />
      </div>

      {isAddDrawerOpen && (
        <AddConfigurationDrawer
          isDrawerOpen={isAddDrawerOpen}
          onClose={() => {
            setIsAddDrawerOpen(false);
          }}
          onSuccess={handleSuccessClick}
          handleQueryCopy={handleQueryCopy}
          configurationData={editConfigurationData || undefined}
        />
      )}

      {isEditDrawerOpen && (
        <EditConfigurationDrawer
          isDrawerOpen={isEditDrawerOpen}
          onClose={handleCloseEditDrawer}
          onSuccess={handleEditDrawerSuccess}
          handleQueryCopy={handleQueryCopy}
        />
      )}
    </div>
  );
};

export default CiuAllocations;
